import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { BookOpen, Sparkles, ClipboardList, Send, Layers, MessagesSquare } from 'lucide-react';
import { features } from '../data/features';

const iconMap = { BookOpen, Sparkles, ClipboardList, Send, Layers, MessagesSquare };

const SLIDES = [
  { type: 'welcome' },
  ...features.map((f) => ({ type: 'feature', feature: f })),
];

const Onboarding = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [animDir, setAnimDir] = useState(null); // 'in' | null

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, []);

  const goNext = () => {
    if (step < SLIDES.length - 1) {
      setAnimDir('in');
      setTimeout(() => setAnimDir(null), 300);
      setStep((s) => s + 1);
    } else {
      onComplete?.();
    }
  };

  const isLast = step === SLIDES.length - 1;
  const slide = SLIDES[step];

  return (
    <div className="absolute inset-0 flex flex-col" style={{ backgroundColor: '#efece4' }}>
      {/* Slide content */}
      <div
        className="flex-1 flex flex-col items-stretch justify-center px-5 pb-4 overflow-hidden"
        style={{
          opacity: animDir === 'in' ? 0 : 1,
          transform: animDir === 'in' ? 'translateX(18px)' : 'translateX(0)',
          transition: 'opacity 0.25s ease, transform 0.25s ease',
        }}
      >
        {slide.type === 'welcome' ? (
          <WelcomeSlide />
        ) : (
          <FeatureSlide feature={slide.feature} index={step - 1} />
        )}
      </div>

      {/* CTA */}
      <div className="px-5 pb-7 flex-shrink-0">
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mb-4">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              aria-label={`Go to slide ${i + 1}`}
              style={{
                width: i === step ? 20 : 6,
                height: 6,
                borderRadius: 9999,
                backgroundColor: i === step ? '#064e3b' : 'rgba(0,0,0,0.18)',
                transition: 'all 0.25s ease',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
        <button
          onClick={goNext}
          className="w-full py-3.5 text-white font-semibold text-[11px] uppercase bg-[#064e3b] transition-opacity active:opacity-80"
          style={{ borderRadius: '14px', letterSpacing: '1.5px', boxShadow: '0 3px 10px rgba(6,78,59,0.30)' }}
        >
          {isLast ? 'Get Started' : 'Next'}
        </button>
        {!isLast && (
          <button
            onClick={onComplete}
            className="w-full mt-2.5 py-2 text-[11px] uppercase font-semibold text-center"
            style={{ letterSpacing: '1px', color: 'rgba(0,0,0,0.35)', background: 'none', border: 'none' }}
          >
            Skip
          </button>
        )}
      </div>
    </div>
  );
};

const WelcomeSlide = () => (
  <div
    className="w-full text-center px-7 py-10 mx-auto"
    style={{
      backgroundColor: '#F8F5EE',
      borderRadius: '16px',
      border: '1px solid rgba(0,0,0,0.08)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
    }}
  >
    <p className="text-[10px] uppercase font-semibold mb-4" style={{ letterSpacing: '2px', color: 'rgba(0,0,0,0.40)' }}>
      Welcome
    </p>
    <h1 className="font-brand text-[72px] leading-none text-[#064e3b]" style={{ letterSpacing: '-0.5px' }}>
      escarole
    </h1>
    <div className="my-5 flex flex-col gap-[3px]">
      <div className="h-[3px] bg-[#064e3b] rounded-full" />
      <div className="h-[1px] bg-[#064e3b] rounded-full opacity-50" />
    </div>
    <p className="text-[15px] leading-[24px]" style={{ color: 'rgba(0,0,0,0.55)' }}>
      The healthier way to scroll.
    </p>
  </div>
);

const cardTones = ['#FBFAF7', '#F6F3EB', '#F9F7F0', '#F4F1E8', '#FAFAF6', '#F7F4EC'];

const FeatureSlide = ({ feature, index }) => {
  const IconComponent = iconMap[feature.icon];
  return (
    <div
      className="w-full px-7 py-8 mx-auto flex flex-col"
      style={{
        backgroundColor: cardTones[index % cardTones.length],
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
        minHeight: 260,
      }}
    >
      {/* Feature number + icon */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-[10px] uppercase font-semibold" style={{ letterSpacing: '2px', color: 'rgba(0,0,0,0.35)' }}>
          {String(index + 1).padStart(2, '0')} / {String(features.length).padStart(2, '0')}
        </p>
        {IconComponent && (
          <div
            className="w-10 h-10 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(6,78,59,0.10)', borderRadius: '10px', border: '1px solid rgba(6,78,59,0.15)' }}
          >
            <IconComponent className="w-5 h-5" style={{ color: '#064e3b' }} strokeWidth={1.8} />
          </div>
        )}
      </div>

      {/* Title + description */}
      <h2 className="font-instrument text-[34px] leading-[1.05] text-[#111] mb-3" style={{ letterSpacing: '-0.3px' }}>
        {feature.title}
      </h2>
      <p className="text-[15px] leading-[1.6]" style={{ color: 'rgba(0,0,0,0.60)' }}>
        {feature.description}
      </p>
    </div>
  );
};

Onboarding.propTypes = {
  onComplete: PropTypes.func.isRequired,
};

FeatureSlide.propTypes = {
  feature: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

export default Onboarding;
